import { useState } from 'react';
import { codeLabel } from '../../utils/codeLabel';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { operationalQueryPolicy } from '../../api/queryPolicies';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getAdminInterestDetail,
  adminInterestAction,
} from '../../api/endpoints/admin';
import type {
  AdminInterestDetailResponse,
  AdminInterestActionRequest,
} from '../../api/types';
import type { DnmRequestFailure } from '../../api/dnmFailure';
import { StatusPill } from '../../components/ui/StatusPill';
import { interestStatusColor } from '../../components/domain/statusColor';
import { useMutationHook } from '../../hooks/useMutationHook';
import styles from './InterestDetailModal.module.css';
import formStyles from '../../components/domain/FormModal.module.css';
import { DatePicker } from '../../components/ui/DatePicker';
import { DialogShell } from '../../components/ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import { DialogHelp } from '../../help/DialogHelp';

interface Props {
  interestId: number;
  onClose: () => void;
  onConvertToBid?: (interest: AdminInterestDetailResponse) => void;
  returnNavState?: { from: string; fromLabel: string; restoreState?: unknown };
}

type ActiveActionForm = 'RECORD_CONTACT' | 'SCHEDULE_FOLLOW_UP' | 'CLOSE' | null;

const CLOSE_REASONS = [
  { value: 'RATE_MISMATCH', label: 'Rate mismatch' },
  { value: 'CAPACITY_UNAVAILABLE', label: 'Capacity unavailable' },
  { value: 'DUPLICATE', label: 'Duplicate interest' },
  { value: 'NO_RESPONSE', label: 'No response from partner' },
  { value: 'VENDOR_CANCELLED', label: 'Partner cancelled interest' },
  { value: 'OTHER', label: 'Other' },
];

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function InterestDetailModal({ interestId, onClose, onConvertToBid, returnNavState }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [activeAction, setActiveAction] = useState<ActiveActionForm>(null);
  const [conflictError, setConflictError] = useState(false);
  const [ownershipFollowsOrderError, setOwnershipFollowsOrderError] = useState(false);

  // Form states
  const [internalNote, setInternalNote] = useState('');
  const [nextFollowUpAt, setNextFollowUpAt] = useState('');
  const [followUpDateError, setFollowUpDateError] = useState(false);
  const [publishToVendor, setPublishToVendor] = useState(false);
  const [publicMessage, setPublicMessage] = useState('');
  const [closedReason, setClosedReason] = useState('RATE_MISMATCH');

  const {
    data: detail,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'interests', interestId],
    queryFn: () => getAdminInterestDetail(interestId),
    ...operationalQueryPolicy,
  });

  const actionMut = useMutationHook(
    async (payload: AdminInterestActionRequest) => {
      try {
        setConflictError(false);
        setOwnershipFollowsOrderError(false);
        return await adminInterestAction(interestId, payload);
      } catch (err: unknown) {
        const failure = err as Partial<DnmRequestFailure>;
        if (failure?.code === 'OWNERSHIP_FOLLOWS_ORDER') {
          setOwnershipFollowsOrderError(true);
        } else if (failure?.response?.status === 409) {
          setConflictError(true);
        }
        throw err;
      }
    },
    {
      successMessage: 'Action saved successfully.',
      invalidateKeys: [
        ['admin', 'interests'],
        ['admin', 'interests', interestId],
      ],
      onSuccess: () => {
        setActiveAction(null);
        setInternalNote('');
        setNextFollowUpAt('');
        setFollowUpDateError(false);
        setPublishToVendor(false);
        setPublicMessage('');
      },
    }
  );

  // Read-only view: quick dismiss. An open action form makes it a form dialog.
  const actionDirty = useIsDirty({ internalNote, nextFollowUpAt, publishToVendor, publicMessage, closedReason });
  const dismiss = useDialogDismiss({
    kind: activeAction ? 'form' : 'confirm',
    dirty: actionDirty,
    busy: actionMut.isLoading,
    onClose,
  });

  if (isLoading) {
    return (
      <DialogShell dismiss={dismiss} label={`Loading interest #${interestId}`} scrimClassName={styles.scrim} panelClassName={styles.panel}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t('interests.interestDetailModal.loadingTitle', { id: interestId })}</h3>
          <button className={styles.closeBtn} onClick={dismiss.requestClose}>✕</button>
        </div>
      </DialogShell>
    );
  }

  if (isError || !detail) {
    return (
      <DialogShell dismiss={dismiss} label={t('interests.interestDetailModal.notFound')} scrimClassName={styles.scrim} panelClassName={styles.panel}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t('interests.interestDetailModal.notFound')}</h3>
          <button className={styles.closeBtn} onClick={dismiss.requestClose}>✕</button>
        </div>
        <div style={{ color: 'var(--status-red-text)' }}>{t('interests.interestDetailModal.loadFailed')}</div>
      </DialogShell>
    );
  }

  const isTerminal =
    detail.status === 'CONVERTED_TO_BID' ||
    detail.status === 'CLOSED' ||
    detail.status === 'WITHDRAWN';

  const handleActionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!detail) return;

    if (activeAction === 'RECORD_CONTACT') {
      actionMut.mutate({
        action: 'RECORD_CONTACT',
        expectedVersion: detail.version,
        internalNote: internalNote.trim() || undefined,
        publishToVendor,
        publicMessage: publishToVendor ? publicMessage.trim() || undefined : undefined,
      });
    } else if (activeAction === 'SCHEDULE_FOLLOW_UP') {
      if (!nextFollowUpAt) {
        setFollowUpDateError(true);
        return;
      }
      // Convert local datetime to ISO string
      const isoDate = new Date(nextFollowUpAt).toISOString();
      actionMut.mutate({
        action: 'SCHEDULE_FOLLOW_UP',
        expectedVersion: detail.version,
        nextFollowUpAt: isoDate,
        internalNote: internalNote.trim() || undefined,
        publishToVendor,
        publicMessage: publishToVendor ? publicMessage.trim() || undefined : undefined,
      });
    } else if (activeAction === 'CLOSE') {
      actionMut.mutate({
        action: 'CLOSE',
        expectedVersion: detail.version,
        closedReason,
        internalNote: internalNote.trim() || undefined,
      });
    }
  };

  const statusColors = interestStatusColor(detail.status);

  return (
    <DialogShell dismiss={dismiss} label={`Interest #${detail.id}`} scrimClassName={styles.scrim} panelClassName={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>{t('interests.interestDetailModal.title', { id: detail.id })}</h3>
          <StatusPill
            label={codeLabel('interestStatus', detail.status)}
            bg={statusColors.bg}
            text={statusColors.text}
          />
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            {t('interests.interestDetailModal.version', { version: detail.version })}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DialogHelp context="interests" title={t('interests.interestDetailModal.title', { id: detail.id })} />
          <button className={styles.closeBtn} onClick={dismiss.requestClose}>✕</button>
        </div>
      </div>

      {/* 409 Conflict Banner */}
      {conflictError && (
        <div className={styles.conflictBanner}>
          <span>
            {t('interests.interestDetailModal.conflict')}
          </span>
          <button
            className={styles.conflictBtn}
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['admin', 'interests', interestId] });
              refetch();
              setConflictError(false);
            }}
          >
            {t('interests.interestDetailModal.refresh')}
          </button>
        </div>
      )}

      {/* Ownership Follows Order Banner */}
      {ownershipFollowsOrderError && (
        <div className={styles.conflictBanner}>
          <span>
            {t('interests.interestDetailModal.ownershipFollowsOrder')}
          </span>
          <Link
            to={`/shipments/${detail.shipmentId}`}
            state={returnNavState ?? { from: location.pathname + location.search, fromLabel: 'Interests' }}
            className={styles.conflictBtn}
            style={{ textDecoration: 'none', display: 'inline-block' }}
          >
            {t('interests.interestDetailModal.goToOrder')}
          </Link>
        </div>
      )}

      {/* Info Grid */}
      <div className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>{t('interests.interestDetailModal.route')}</span>
          <span className={styles.infoValue}>
            <Link
              to={`/shipments/${detail.shipmentId}`}
              state={returnNavState ?? { from: location.pathname + location.search, fromLabel: 'Interests' }}
              style={{ color: 'var(--accent)', fontWeight: 600 }}
            >
              {detail.routeLabel ||
                `${detail.pickupCity || '—'}, ${detail.pickupStateName || detail.pickupState || '—'} → ${detail.dropCity || '—'}, ${detail.dropStateName || detail.dropState || '—'}`}
            </Link>{' '}
            {t('interests.interestDetailModal.cycle', { cycle: detail.postingCycle })}
          </span>
        </div>

        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>{t('interests.interestDetailModal.vendor')}</span>
          <span className={styles.infoValue}>
            <Link to={`/users/${detail.vendorId}`} style={{ color: 'var(--accent)', fontWeight: 600 }}>
              {detail.vendorName || `Partner #${detail.vendorId}`}
            </Link>{' '}
            ({detail.vendorPhone || 'No phone'})
          </span>
        </div>

        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>{t('interests.interestDetailModal.orderOwnerLabel')}</span>
          <span className={styles.infoValue}>
            {detail.ownerAdminName ? (
              <span>
                {t('interests.interestDetailModal.orderOwner')}{' '}
                <Link
                  to={`/shipments/${detail.shipmentId}`}
                  state={returnNavState ?? { from: location.pathname + location.search, fromLabel: 'Interests' }}
                  style={{ color: 'var(--accent)', fontWeight: 600 }}
                >
                  {detail.ownerAdminName}
                </Link>
              </span>
            ) : (
              <Link
                to={`/shipments/${detail.shipmentId}`}
                state={returnNavState ?? { from: location.pathname + location.search, fromLabel: 'Interests' }}
                style={{ color: 'var(--accent)', fontWeight: 600 }}
              >
                {t('interests.interestDetailModal.unassigned')}
              </Link>
            )}
          </span>
        </div>

        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>{t('interests.interestDetailModal.nextFollowUp')}</span>
          <span className={styles.infoValue}>
            {fmtDateTime(detail.nextFollowUpAt)}
          </span>
        </div>

        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>{t('interests.interestDetailModal.expressedAt')}</span>
          <span className={styles.infoValue}>
            {fmtDateTime(detail.createdAt)}
          </span>
        </div>

        {detail.bidId && (
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('interests.interestDetailModal.convertedBidId')}</span>
            <span className={styles.infoValue} style={{ color: 'var(--status-green-text)', fontWeight: 700 }}>
              {t('interests.interestDetailModal.bid', { id: detail.bidId })}
            </span>
          </div>
        )}

        {detail.closedReason && (
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('interests.interestDetailModal.closedReason')}</span>
            <span className={styles.infoValue}>
              {codeLabel('closeReason', detail.closedReason)}
            </span>
          </div>
        )}
      </div>

      {/* Vendor Note */}
      {detail.vendorNote && (
        <div>
          <div className={styles.infoLabel} style={{ marginBottom: 4 }}>{t('interests.interestDetailModal.vendorNote')}</div>
          <div className={styles.noteCard}>{detail.vendorNote}</div>
        </div>
      )}

      {/* Public Feedback info if available */}
      {detail.publicMessage && (
        <div>
          <div className={styles.infoLabel} style={{ marginBottom: 4 }}>{t('interests.interestDetailModal.publishedVendorMessage')}</div>
          <div className={styles.noteCard} style={{ borderLeftColor: 'var(--status-green-text-2)' }}>
            {detail.publicMessage}
            {detail.publicCallbackAt && (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                {t('interests.interestDetailModal.callbackScheduled', { date: fmtDateTime(detail.publicCallbackAt) })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Controls */}
      {!isTerminal && (
        <div>
          <div className={styles.sectionTitle}>{t('interests.interestDetailModal.actions')}</div>
          <div className={styles.actionTabs}>
            <button
              type="button"
              className={`${styles.actionTabBtn} ${activeAction === 'RECORD_CONTACT' ? styles.actionTabActive : ''}`}
              onClick={() => setActiveAction(activeAction === 'RECORD_CONTACT' ? null : 'RECORD_CONTACT')}
            >
              {t('interests.interestDetailModal.recordContact')}
            </button>
            <button
              type="button"
              className={`${styles.actionTabBtn} ${activeAction === 'SCHEDULE_FOLLOW_UP' ? styles.actionTabActive : ''}`}
              onClick={() => setActiveAction(activeAction === 'SCHEDULE_FOLLOW_UP' ? null : 'SCHEDULE_FOLLOW_UP')}
            >
              {t('interests.interestDetailModal.scheduleFollowUp')}
            </button>
            <button
              type="button"
              className={`${styles.actionTabBtn} ${activeAction === 'CLOSE' ? styles.actionTabActive : ''}`}
              onClick={() => setActiveAction(activeAction === 'CLOSE' ? null : 'CLOSE')}
            >
              {t('interests.interestDetailModal.close')}
            </button>
            {onConvertToBid && (
              <button
                type="button"
                className={`${styles.actionTabBtn} ${styles.actionTabConvert}`}
                onClick={() => onConvertToBid(detail)}
              >
                {t('interests.interestDetailModal.convertToBid')}
              </button>
            )}
          </div>

          {/* Subform for selected action */}
          {activeAction && (
            <form onSubmit={handleActionSubmit} className={styles.actionForm} style={{ marginTop: 10 }}>

              {activeAction === 'RECORD_CONTACT' && (
                <div>
                  <label className={styles.formLabel}>{t('interests.interestDetailModal.internalNote')}</label>
                  <textarea
                    className={styles.textarea}
                    placeholder={t('interests.interestDetailModal.contactNotePlaceholder')}
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    required
                  />
                  <label className={styles.checkboxRow} style={{ marginTop: 10 }}>
                    <input
                      type="checkbox"
                      checked={publishToVendor}
                      onChange={(e) => setPublishToVendor(e.target.checked)}
                    />
                    <span>{t('interests.interestDetailModal.publishStatusUpdate')}</span>
                  </label>
                  {publishToVendor && (
                    <div style={{ marginTop: 8 }}>
                      <label className={styles.formLabel}>{t('interests.interestDetailModal.vendorMessage')}</label>
                      <input
                        className={styles.input}
                        placeholder={t('interests.interestDetailModal.vendorMessagePlaceholder')}
                        value={publicMessage}
                        onChange={(e) => setPublicMessage(e.target.value)}
                        required={publishToVendor}
                      />
                    </div>
                  )}
                </div>
              )}

              {activeAction === 'SCHEDULE_FOLLOW_UP' && (
                <div>
                  <label className={styles.formLabel} htmlFor="interest-next-follow-up">{t('interests.interestDetailModal.nextFollowUpDateTime')}</label>
                  <DatePicker
                    withTime
                    id="interest-next-follow-up"
                    className={styles.input}
                    value={nextFollowUpAt}
                    onChange={(value) => {
                      setNextFollowUpAt(value);
                      if (value) setFollowUpDateError(false);
                    }}
                    aria-invalid={followUpDateError || undefined}
                    aria-describedby={followUpDateError ? 'interest-next-follow-up-error' : undefined}
                  />
                  {followUpDateError && (
                    <div id="interest-next-follow-up-error" role="alert" className={formStyles.error}>
                      {t('interests.interestDetailModal.followUpDateRequired')}
                    </div>
                  )}
                  <label className={styles.formLabel} style={{ marginTop: 10 }}>{t('interests.interestDetailModal.internalNoteOptional')}</label>
                  <textarea
                    className={styles.textarea}
                    placeholder={t('interests.interestDetailModal.followUpNotePlaceholder')}
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                  />
                  <label className={styles.checkboxRow} style={{ marginTop: 10 }}>
                    <input
                      type="checkbox"
                      checked={publishToVendor}
                      onChange={(e) => setPublishToVendor(e.target.checked)}
                    />
                    <span>{t('interests.interestDetailModal.publishCallbackSchedule')}</span>
                  </label>
                  {publishToVendor && (
                    <div style={{ marginTop: 8 }}>
                      <label className={styles.formLabel}>{t('interests.interestDetailModal.vendorCallbackMessage')}</label>
                      <input
                        className={styles.input}
                        placeholder={t('interests.interestDetailModal.vendorCallbackPlaceholder')}
                        value={publicMessage}
                        onChange={(e) => setPublicMessage(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              {activeAction === 'CLOSE' && (
                <div>
                  <label className={styles.formLabel}>{t('interests.interestDetailModal.reasonForClosing')}</label>
                  <select
                    className={styles.input}
                    value={closedReason}
                    onChange={(e) => setClosedReason(e.target.value)}
                  >
                    {CLOSE_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <label className={styles.formLabel} style={{ marginTop: 10 }}>{t('interests.interestDetailModal.internalNote')}</label>
                  <textarea
                    className={styles.textarea}
                    placeholder={t('interests.interestDetailModal.closeNotePlaceholder')}
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                  />
                </div>
              )}

              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelActionBtn}
                  onClick={() => {
                    setFollowUpDateError(false);
                    setActiveAction(null);
                  }}
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={actionMut.isLoading}
                >
                  {actionMut.isLoading ? 'Saving…' : 'Save Action'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Audit Event History */}
      <div>
        <div className={styles.sectionTitle}>{t('interests.interestDetailModal.eventHistory', { total: detail.events?.length ?? 0 })}</div>
        {detail.events && detail.events.length > 0 ? (
          <div className={styles.timeline}>
            {detail.events.map((evt) => (
              <div key={evt.id} className={styles.timelineEvent}>
                <div className={styles.timelineDot} />
                <div className={styles.timelineHeader}>
                  <span className={styles.timelineType}>{codeLabel('interestEvent', evt.eventType)}</span>
                  <span className={styles.timelineDate}>{fmtDateTime(evt.createdAt)}</span>
                </div>
                <div className={styles.timelineActor}>
                  {t('interests.interestDetailModal.by', { actor: evt.actorName || evt.actorRole || 'System' })}
                </div>
                {evt.internalNote && (
                  <div className={styles.timelineNote}>{evt.internalNote}</div>
                )}
                {evt.structuredPublicOutcome && (
                  <div className={styles.timelineOutcome}>
                    {t('interests.interestDetailModal.publicOutcome', { outcome: evt.structuredPublicOutcome })}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t('interests.interestDetailModal.noEvents')}</div>
        )}
      </div>
    </DialogShell>
  );
}
